/**
 * Smooth-scroll every signup scroll container to its absolute top
 * (page title and progress tabs), not merely until the first field is visible.
 *
 * Logged-in chrome scrolls a nested overflow-y main pane. The Next button's
 * focus can also pull the first question into view after the step swaps, so
 * that focus is cleared before scrolling.
 *
 * @returns {() => void} Cleanup for pending animation frames and timers
 */
export function scrollSignupToTop() {
  if (typeof document.activeElement?.blur === "function") {
    document.activeElement.blur();
  }

  const run = () => {
    const seen = new Set();

    /**
     * @param {Element|null|undefined} node
     */
    const scrollNode = (node) => {
      if (!node || seen.has(node) || typeof node.scrollTo !== "function") return;
      seen.add(node);
      node.scrollTo({ top: 0, behavior: "smooth" });
    };

    window.scrollTo({ top: 0, behavior: "smooth" });
    scrollNode(document.scrollingElement);
    scrollNode(document.documentElement);
    scrollNode(document.body);

    document.querySelectorAll("main").forEach((node) => scrollNode(node));

    let node = document.getElementById("signup-scroll-top") || document.querySelector("h1");
    while (node && node !== document.documentElement) {
      const overflowY = window.getComputedStyle(node).overflowY;
      if (overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay") {
        scrollNode(node);
      }
      node = node.parentElement;
    }
  };

  run();
  let timeoutId = 0;
  const outerFrame = window.requestAnimationFrame(() => {
    window.requestAnimationFrame(run);
  });
  timeoutId = window.setTimeout(run, 50);

  return () => {
    window.cancelAnimationFrame(outerFrame);
    window.clearTimeout(timeoutId);
  };
}
